// Client half of F8's live plumbing: a single EventSource to /events shared by the
// whole app. Components subscribe per event type; the connection is opened lazily on
// the first subscriber and closed when the last one leaves. The browser reconnects an
// EventSource on transient drops on its own; we additionally recreate it after a hard
// error (readyState CLOSED) with a small backoff, and surface a connection status so
// the UI can show online/offline and the offline queue can flush on reconnect.

export type LiveStatus = 'connecting' | 'open' | 'closed';

type EventHandler = (payload: unknown) => void;
type StatusHandler = (status: LiveStatus) => void;

const RECONNECT_MS = 3_000;

const handlers = new Map<string, Set<EventHandler>>();
const statusHandlers = new Set<StatusHandler>();
const registered = new Set<string>(); // event types wired onto the current source

let source: EventSource | null = null;
let status: LiveStatus = 'closed';
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function setStatus(next: LiveStatus): void {
  if (status === next) return;
  status = next;
  for (const cb of statusHandlers) cb(next);
}

export function liveStatus(): LiveStatus {
  return status;
}

function attach(type: string): void {
  if (!source || registered.has(type)) return;
  source.addEventListener(type, (ev: MessageEvent) => {
    let payload: unknown = null;
    try {
      payload = ev.data ? JSON.parse(ev.data) : null;
    } catch {
      payload = ev.data;
    }
    for (const cb of handlers.get(type) ?? []) cb(payload);
  });
  registered.add(type);
}

function connect(): void {
  if (source) return;
  setStatus('connecting');
  registered.clear();
  // Same-origin request; the httpOnly session cookie rides along automatically.
  source = new EventSource('/events');
  source.onopen = () => setStatus('open');
  source.onerror = () => {
    // EventSource retries CONNECTING drops itself; only intervene on a hard close.
    if (source && source.readyState === EventSource.CLOSED) {
      teardown();
      setStatus('closed');
      if (handlers.size > 0 && !reconnectTimer) {
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          connect();
        }, RECONNECT_MS);
      }
    } else {
      setStatus('connecting');
    }
  };
  for (const type of handlers.keys()) attach(type);
}

function teardown(): void {
  if (source) {
    source.close();
    source = null;
  }
  registered.clear();
}

function maybeDisconnect(): void {
  if (handlers.size === 0) {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    teardown();
    setStatus('closed');
  }
}

/** Subscribe to a named live event. Returns an unsubscribe function. */
export function subscribe(type: string, handler: EventHandler): () => void {
  let set = handlers.get(type);
  if (!set) {
    set = new Set();
    handlers.set(type, set);
  }
  set.add(handler);
  connect();
  attach(type);
  return () => {
    const s = handlers.get(type);
    if (!s) return;
    s.delete(handler);
    if (s.size === 0) handlers.delete(type);
    maybeDisconnect();
  };
}

/** Subscribe to connection-status changes. Fires immediately with the current status. */
export function onStatus(handler: StatusHandler): () => void {
  statusHandlers.add(handler);
  handler(status);
  return () => statusHandlers.delete(handler);
}
